import { filter, switchMap, takeWhile, tap } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Location } from '@angular/common';
import { PasswordRequirement } from '../common/gql/types';
import { ApplicationType, WelcomeService } from '../common/services/welcome.service';
import { AuthService } from '../common/services/auth.service';
import { DynamicConfigService } from '../common/services/dynamic-config.service';
import { RouteStorageService } from '../common/services/route-storage.service';
import { StorageService } from '../common/services/storage.service';
import { WindowInjection } from '../common/window';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PasswordToggleComponent } from '../common/form-components/password-toggle/password-toggle.component';
import { DialogService } from '../common/services/dialog.service';
import { ForgotPasswordConfirmationModalComponent } from '../common/modals/forgot-password-confirmation-modal.component';
import { environment } from '../../environments/environment';
import { BrandingService } from '../common/services/branding.service';

const USERNAME_STORAGE_KEY = 'sc-un';
const USERNAME_STORAGE_EXPIRE_DAYS = 30; // 30 days is 2592000000 ms

interface StoredUsernameData {
  email?: string;
  dateMS?: number;
}

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, FormsModule, ReactiveFormsModule, FaIconComponent, PasswordToggleComponent]
})
export class WelcomeComponent implements OnInit, OnDestroy {
  // Auto-focus the password input when it comes into view
  @ViewChild('passwordField') set passwordField(el: ElementRef) {
    if (el) {
      el.nativeElement.focus();
    }
  }
  private alive: boolean = true;
  // Need to keep track of when the email field is focused so we know if it's being filled by a browser extension or not
  private emailFieldFocused: boolean;
  protected emailRegex: RegExp = new RegExp('^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$');
  public loading = false;
  public loginEmail: string;
  public errorMsg: string;
  public focus: string;
  public forgotPasswordEmail: string;
  public validationKey: string;
  public emailText: string;
  public setNewPasswordText: string;
  public setRetypeNewPasswordText: string;
  public pathName: string;
  public nextPath: string;
  public changePasswordMessageType?: 'create' | 'change' | 'business-terms' | 'rafiki-terms';
  public acceptedTermsAndConditions = false;
  passwordRequirements: PasswordRequirement[];
  public enablePasswordManagerBypass: boolean = false; // We use this is to prevent an autofill bug with password manager extensions

  @ViewChild('loginAfterForm')
  loginAfterForm;
  loginAfterEmailText: string;
  loginAfterPasswordText: string;

  createAccountApplicationTypes: ApplicationType[];

  startApplicationPasswordText: string;

  showSso: boolean;
  useSso: boolean;
  ssoLoginEmail: string;

  public loginForm: UntypedFormGroup;
  public passwordRequired: boolean;
  public signUpUrl = `${environment.signUpUrl}?utm_source=login-page`;

  private storedUsernameData: StoredUsernameData;

  constructor(
    public welcomeService: WelcomeService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastrService,
    public authService: AuthService,
    public dynamicConfig: DynamicConfigService,
    private routeStorage: RouteStorageService,
    private storage: StorageService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: DialogService,
    public brandingService: BrandingService,
    @Inject(WindowInjection) private window: Window
  ) {}

  ngOnInit(): void {
    this.initLoginForm();
    this.pathName = this.route.snapshot.url.join('');
    this.route.queryParams.pipe(takeWhile(() => this.alive)).subscribe((params) => {
      // PIN has been deprecated and will be treated like a validation-key
      // the user needs to create their own password
      this.validationKey = params['validation-key'] || params['pin'];
      this.emailText = params['email'];
      if (params['error'] === 'sso') {
        this.toastService.error('Please login with SSO');
      }
      if (params['next']) {
        this.routeStorage.saveRoute(params['next']);
      }
      this.nextPath = params['next'];
      if (this.nextPath) {
        const urlTree = this.router.parseUrl(this.nextPath);
        this.nextPath = `/${urlTree.root.children['primary']?.segments.map((it) => it.path).join('/')}`;
      }
      this.changePasswordMessageType = params['message'];

      if (params['theme']) {
        this.brandingService.applyThemeFromParam(params['theme']);
      }

      if (params['pin'] && !params['message']) {
        this.changePasswordMessageType = 'create';
      }

      if (this.validationKey && this.emailText) {
        this.focus = 'newpassword';
        this.welcomeService.checkValidationKey(this.validationKey).subscribe({
          next: ({ valid, use_sso }: { valid: boolean; use_sso: boolean }) => {
            this.useSso = use_sso;
            if (!valid || use_sso) {
              this.focus = null;
              this.router.navigate(['/welcome']);
            } else {
              this.fetchPasswordRequirements();
            }
          },
          error: (error) => {
            this.focus = null;
            this.router.navigate(['/welcome']);
          }
        });
      }

      if (params['e']) {
        const msg = this.authService.authErrorMessages[params['e']] || 'There was a problem logging in.';
        // https://github.com/scttcper/ngx-toastr#faq
        setTimeout(() => this.toastService.error(msg, 'Login Error'));
      }
    });

    if (this.pathName === 'welcome' && this.emailText) {
      this.loginForm.patchValue({ email: this.emailText });
    }

    if (this.pathName === 'forgot-password') {
      this.focus = 'forgotpassword';
      this.forgotPasswordEmail = this.emailText;
    }

    this.welcomeService.getApplicationTypes();
    this.welcomeService.applicationTypes
      .pipe(takeWhile(() => this.alive))
      .subscribe((applicationTypes: ApplicationType[]) => {
        this.createAccountApplicationTypes = applicationTypes.sort((type1, type2) =>
          type1.name.localeCompare(type2.name)
        );
      });

    this.dynamicConfig.dynamicConfig.pipe(takeWhile(() => this.alive)).subscribe(({ show_sso: showSso }) => {
      this.showSso = showSso;
    });
  }

  initLoginForm() {
    const email = this.checkStoredUsername();
    this.loginForm = this.formBuilder.group({
      email: [email, Validators.required],
      password: [''],
      rememberUsername: [!!email]
    });

    // We need to watch the email field for changes and reset the form if the password is required
    // Should only fire if the user manually changes the email. Not when an extension fills it
    this.loginForm
      .get('email')
      .valueChanges.pipe(filter(() => this.passwordRequired))
      .subscribe(() => {
        this.passwordRequired = false;
        this.enablePasswordManagerBypass = false;
        const passwordField = this.loginForm.get('password');
        passwordField.patchValue('');
        passwordField.clearValidators();
        passwordField.setErrors(null);
      });
  }

  fetchPasswordRequirements() {
    this.authService.passwordRequirements(this.emailText).subscribe({
      next: (requirements) => {
        this.passwordRequirements = requirements;
      },
      error: (error) => {
        if (error && this.useSso) {
          this.focus = 'sso';
        }
      }
    });
  }

  checkStoredUsername() {
    this.storedUsernameData = this.storage.getObject(USERNAME_STORAGE_KEY) as StoredUsernameData;

    if (!this.storedUsernameData) {
      return '';
    }

    const { email, dateMS } = this.storedUsernameData;
    const expireTimeMS = USERNAME_STORAGE_EXPIRE_DAYS * 24 * 60 * 60 * 1000;
    const nowMS = new Date().getTime();
    const diffMS = nowMS - dateMS;
    if (diffMS < expireTimeMS) {
      return email;
    }

    this.storage.remove(USERNAME_STORAGE_KEY);
    this.storedUsernameData = null;

    return '';
  }

  forgotPassword() {
    if (this.forgotPasswordEmail) {
      this.loading = true;
      this.authService
        .forgotPassword(this.forgotPasswordEmail)
        .pipe(
          tap(() => (this.loading = false)),
          switchMap(() => this.dialog.open(ForgotPasswordConfirmationModalComponent, {}).afterClosed()),
          takeWhile(() => this.alive)
        )
        .subscribe(() => {
          this.router.navigate(['welcome'], { queryParams: { next: this.nextPath } });
        });
    } else {
      this.toastService.error('Something went wrong, Unable to send reset password request');
    }
  }

  setNewPassword() {
    if (this.useSso) {
      this.ssoLoginEmail = this.emailText;
      this.ssoLogin();
    } else {
      this.errorMsg = '';
      if (this.setNewPasswordText) {
        this.loading = true;
        this.authService
          .setNewPassword(this.setNewPasswordText, this.validationKey)
          .pipe(takeWhile(() => this.alive))
          .subscribe({
            next: (u) => {
              this.loading = false;

              this.loginAfterEmailText = this.emailText;
              this.loginAfterPasswordText = this.setNewPasswordText;
              setTimeout(() => {
                this.loginAfterForm.nativeElement.submit();
              }, 10);
            },
            error: (e) => {
              this.loading = false;

              if (typeof e.error === 'string') {
                this.errorMsg = e.error;
              } else {
                e.error.forEach((errMsg) => this.toastService.error(errMsg));
              }
            }
          });
      } else {
        this.toastService.error('Unable to set new password', 'New Password Error');
      }
    }
  }

  ssoLogin() {
    if (!this.ssoLoginEmail) {
      this.toastService.info('Please enter your organization email address.');
      return;
    }
    this.authService.getSocialLoginURL(this.ssoLoginEmail, this.nextPath).subscribe({
      next: (url) => (this.window.location = url),
      error: (e) => this.toastService.error(e.error)
    });
  }

  setStoredUsername() {
    const { email, rememberUsername } = this.loginForm.value;

    if (rememberUsername) {
      this.updateStoredUsername(email);
    } else {
      this.storage.remove(USERNAME_STORAGE_KEY);
    }
  }

  updateStoredUsername(newEmail: string) {
    const newDateMS = new Date().getTime();
    const data: StoredUsernameData = {
      email: newEmail,
      dateMS: newDateMS
    };

    // If the email has changed, we'll set a new expiration date
    // otherwise, we'll just keep the old one
    if (this.storedUsernameData?.email) {
      const { email: oldEmail, dateMS: oldDateMS } = this.storedUsernameData;
      data.dateMS = oldEmail === newEmail ? oldDateMS : newDateMS;
    }

    this.storage.putObject(USERNAME_STORAGE_KEY, data);
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  submitEmailPasswordForm(form) {
    this.setStoredUsername();
    // We need to disable the password manager bypass so the form sends the correct
    // input fields. Run this.changeDetectorRef.detectChanges() to make sure the form
    // is updated before we submit it.
    this.enablePasswordManagerBypass = false;
    this.changeDetectorRef.detectChanges();
    form.submit();
  }

  checkEmail(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.loginForm.valid) {
      const { email } = this.loginForm.value;

      this.authService.getSocialLoginURL(email, this.nextPath).subscribe({
        next: (response) => {
          if (response) {
            this.setStoredUsername();
            this.window.location = response;
          } else {
            this.passwordRequired = true;
            this.enablePasswordManagerBypass = true;
          }
        },
        error: (e) => {
          this.toastService.error(e.error);
        }
      });
    }
  }
}
